import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { FileSelectEvent, FileUploadModule } from 'primeng/fileupload';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { Company } from '../company.model';
import { CompanyService } from '../company.service';
import { SessionStorageService } from '../../helpers/session-storage.service';
import { StorageService } from '../../services/storage.service';
import { LocationSearchComponent } from '../../shared/location-search/location-search.component';
import { SharedEndpointService } from '../../services/shared-endpoint.service';
import { Country } from '../../models/common/country.model';
import { LatLng } from '../../models/common/lat-lng.model';

@Component({
  selector: 'app-company-edit',
  standalone: true,
  templateUrl: './company-edit.component.html',
  styleUrls: ['../../shared/management-pages.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    ButtonModule,
    FileUploadModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    SelectModule,
    LocationSearchComponent,
    ToastModule
  ],
  providers: [MessageService]
})
export class CompanyEditComponent {
  private readonly fb = inject(FormBuilder);
  private readonly companyService = inject(CompanyService);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly storageService = inject(StorageService);
  private readonly sharedEndpointService = inject(SharedEndpointService);

  company = signal<Company | null>(null);
  countries = signal<Country[]>([]);
  selectedLocation = signal<LatLng | null>(null);
  isLoading = signal(false);
  isSaving = signal(false);
  isUploadingLogo = signal(false);
  error = signal<string | null>(null);

  form = this.fb.group({
    searchLocation: [''],
    name: [{ value: '', disabled: true }, Validators.required],
    crNumber: [{ value: null as number | null, disabled: true }, Validators.required],
    taxRegNumber: [{ value: '', disabled: true }, Validators.required],
    logo: [''],
    phoneNumber: [''],
    emailAddress: ['', Validators.email],
    websiteURL: [''],
    address: [''],
    poBox: [null as number | null],
    countryId: [null as number | null],
    placeId: ['']
  });

  onLogoUpload(event: FileSelectEvent): void {
    const currentCompany = this.company();
    if (!event.files?.length || !currentCompany?.id) {
      return;
    }

    const file = event.files[0];
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const filePath = `company/images/${currentCompany.id}/logo_${Date.now()}_${sanitizedName}`;

    this.isUploadingLogo.set(true);
    this.storageService.uploadFile(filePath, file).pipe(
      finalize(() => this.isUploadingLogo.set(false))
    ).subscribe({
      next: url => {
        this.form.patchValue({ logo: url });
        this.messageService.add({ severity: 'success', summary: 'Logo uploaded', detail: 'Company logo is ready to save.' });
      },
      error: error => this.messageService.add({
        severity: 'error',
        summary: 'Upload failed',
        detail: error?.message || 'Unable to upload company logo.'
      })
    });
  }

  ngOnInit(): void {
    this.loadCountries();

    const sessionCompany = this.sessionStorage.getCompany();
    if (!sessionCompany?.id) {
      this.error.set('No company context found. Please log in again.');
      return;
    }

    this.isLoading.set(true);
    this.companyService.getCompanyById(sessionCompany.id).pipe(
      finalize(() => this.isLoading.set(false))
    ).subscribe({
      next: response => {
        const company = response.data;
        this.company.set(company);
        this.form.patchValue({
          name: company.name,
          crNumber: company.crNumber,
          taxRegNumber: company.taxRegNumber,
          logo: company.logo,
          phoneNumber: company.phoneNumber,
          emailAddress: company.emailAddress,
          websiteURL: company.websiteURL,
          address: company.address,
          poBox: company.poBox,
          countryId: company.countryId,
          placeId: company.placeId
        });
        this.selectedLocation.set(company.location ?? null);
      },
      error: error => this.error.set(error?.message || 'Unable to load company.')
    });
  }

  placeSelect(place: google.maps.places.Place): void {
    const latitude = this.readCoordinate(place.location, 'lat');
    const longitude = this.readCoordinate(place.location, 'lng');
    if (latitude !== null && longitude !== null) {
      this.selectedLocation.set(new LatLng(latitude, longitude, place.displayName ?? place.formattedAddress ?? ''));
    }

    const countryCode = this.readCountryCode(place);
    if (countryCode) {
      this.sharedEndpointService.findCountryByCode(countryCode, 'en-US').subscribe({
        next: country => this.form.patchValue({ countryId: country?.id ?? this.form.getRawValue().countryId }),
        error: () => undefined
      });
    }

    this.form.patchValue({
      name: place.displayName ?? this.form.getRawValue().name,
      phoneNumber: place.internationalPhoneNumber?.replace(/\s/g, '') ?? this.form.getRawValue().phoneNumber,
      emailAddress: (place as any).emailAddress ?? (place as any).email ?? this.form.getRawValue().emailAddress,
      websiteURL: place.websiteURI ?? this.form.getRawValue().websiteURL,
      address: place.formattedAddress ?? this.form.getRawValue().address,
      placeId: place.id ?? this.form.getRawValue().placeId,
      logo: place.photos?.[0]?.getURI() ?? this.form.getRawValue().logo
    });
  }

  save(): void {
    const original = this.company();
    if (!original) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const payload: Company = {
      ...original,
      name: value.name ?? original.name,
      crNumber: original.crNumber,
      taxRegNumber: original.taxRegNumber,
      logo: value.logo,
      phoneNumber: value.phoneNumber,
      emailAddress: value.emailAddress,
      websiteURL: value.websiteURL,
      address: value.address ?? '',
      poBox: value.poBox,
      countryId: value.countryId ?? original.countryId,
      placeId: value.placeId,
      location: this.selectedLocation() ?? original.location
    };

    this.isSaving.set(true);
    this.companyService.updateCompany(payload).pipe(
      finalize(() => this.isSaving.set(false))
    ).subscribe({
      next: response => {
        if (!response.success) {
          this.messageService.add({ severity: 'error', summary: 'Save failed', detail: response.message });
          return;
        }
        this.sessionStorage.saveCustomer({
          ...(this.sessionStorage.getCustomer() as any),
          company: response.data
        });
        this.messageService.add({ severity: 'success', summary: 'Company saved', detail: response.message });
        this.router.navigate(['/company']);
      },
      error: error => this.messageService.add({
        severity: 'error',
        summary: 'Save failed',
        detail: error?.message || 'Unable to save company.'
      })
    });
  }

  private loadCountries(): void {
    this.sharedEndpointService.findAllCountries('en-US').subscribe({
      next: countries => this.countries.set(countries ?? []),
      error: error => this.messageService.add({
        severity: 'warn',
        summary: 'Countries unavailable',
        detail: error?.message || 'Unable to load country list.'
      })
    });
  }

  private readCoordinate(location: google.maps.LatLng | google.maps.LatLngLiteral | null | undefined, axis: 'lat' | 'lng'): number | null {
    if (!location) {
      return null;
    }
    const value = (location as any)[axis];
    if (typeof value === 'function') {
      return value.call(location);
    }
    return typeof value === 'number' ? value : null;
  }

  private readCountryCode(place: google.maps.places.Place): string | null {
    const country = place.addressComponents?.find(component => component.types.includes('country'));
    return country?.shortText ?? (country as any)?.short_name ?? null;
  }
}
